import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import fdbService from '../../services/fdbService';
import DrugImageThumbnail from './DrugImageThumbnail';

interface DrugInfoDialogProps {
  open: boolean;
  onClose: () => void;
  din: string;
}

/**
 * Dialog that shows drug image + FDB info when a DIN is clicked.
 * Used app-wide anywhere a DIN is displayed as a clickable link.
 */
export default function DrugInfoDialog({ open, onClose, din }: DrugInfoDialogProps) {
  const { data: drugInfo, isLoading: drugInfoLoading, error: drugInfoError } = useQuery({
    queryKey: ['fdb', 'drug', din],
    queryFn: () => fdbService.getDrugInfo(din),
    enabled: open && !!din,
    staleTime: 30 * 60 * 1000, // 30 min
  });

  const { data: warnings } = useQuery({
    queryKey: ['fdb', 'warnings', din],
    queryFn: () => fdbService.getDrugWarnings(din),
    enabled: open && !!din,
    staleTime: 30 * 60 * 1000,
  });

  const { data: sideEffects } = useQuery({
    queryKey: ['fdb', 'sideEffects', din],
    queryFn: () => fdbService.getDrugSideEffects(din),
    enabled: open && !!din,
    staleTime: 30 * 60 * 1000,
  });

  const { data: indications } = useQuery({
    queryKey: ['fdb', 'indications', din],
    queryFn: () => fdbService.getDrugIndications(din),
    enabled: open && !!din,
    staleTime: 30 * 60 * 1000,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6" component="span">Drug Information</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {drugInfoLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {drugInfoError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            FDB data not available for DIN {din}. This drug may not be in the First DataBank database.
          </Alert>
        )}

        {/* Drug Image + Basic Info Header */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <DrugImageThumbnail din={din} size={150} borderRadius={8} showPlaceholder expandable />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {drugInfo ? (
              <>
                <Typography variant="h6" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                  {drugInfo.brandName || drugInfo.labelName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {drugInfo.genericName}
                  {drugInfo.strength ? ` ${drugInfo.strength}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                  <Chip label={`DIN: ${din}`} size="small" color="primary" variant="outlined" />
                  {drugInfo.routeOfAdministration && (
                    <Chip label={drugInfo.routeOfAdministration} size="small" variant="outlined" />
                  )}
                  {drugInfo.dosageFormCode && (
                    <Chip label={drugInfo.dosageFormCode} size="small" variant="outlined" />
                  )}
                </Box>
                {drugInfo.manufacturerDesc && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Manufacturer: {drugInfo.manufacturerDesc}
                  </Typography>
                )}
                {drugInfo.ahfsDescription && (
                  <Typography variant="caption" color="text.disabled" display="block">
                    Classification: {drugInfo.ahfsDescription}
                  </Typography>
                )}
              </>
            ) : !drugInfoLoading ? (
              <Box>
                <Typography variant="h6" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                  DIN: {din}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No FDB data available
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>

        {/* Warnings */}
        {warnings && warnings.warnings.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" color="warning.main" sx={{ mb: 0.5 }}>
              Warnings ({warnings.warnings.length})
            </Typography>
            {warnings.warnings.slice(0, 5).map((w, i) => (
              <Typography key={i} variant="caption" display="block" sx={{ mb: 0.25, pl: 1 }}>
                {w.text}
              </Typography>
            ))}
            {warnings.warnings.length > 5 && (
              <Typography variant="caption" color="text.disabled" sx={{ pl: 1 }}>
                + {warnings.warnings.length - 5} more warnings
              </Typography>
            )}
          </>
        )}

        {/* Side Effects (top 5) */}
        {sideEffects && sideEffects.sideEffects.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" color="error.main" sx={{ mb: 0.5 }}>
              Common Side Effects
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {sideEffects.sideEffects.slice(0, 8).map((se, i) => (
                <Chip
                  key={i}
                  label={se.description}
                  size="small"
                  variant="outlined"
                  color={se.severity === '1' ? 'error' : se.severity === '2' ? 'warning' : 'default'}
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              ))}
              {sideEffects.sideEffects.length > 8 && (
                <Chip
                  label={`+${sideEffects.sideEffects.length - 8} more`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </>
        )}

        {/* Indications */}
        {indications && indications.indications && indications.indications.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" color="primary.main" sx={{ mb: 0.5 }}>
              Indications
            </Typography>
            {indications.indications.slice(0, 6).map((ind, i) => (
              <Typography key={i} variant="caption" display="block" sx={{ mb: 0.25, pl: 1 }}>
                {ind.diseaseDescription}
              </Typography>
            ))}
            {indications.indications.length > 6 && (
              <Typography variant="caption" color="text.disabled" sx={{ pl: 1 }}>
                + {indications.indications.length - 6} more
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
